import { useEffect, useRef, useState } from 'react'

// UX56 (revised UX07): 3-step swipeable intro for first-visit users.
//
// Scope from the UX framework strategic batch — Welcome flow polish.
// Collapses the previous dense welcome paragraph into three bite-sized
// "steps" the user can swipe through. Final step CTA opens the
// jurisdiction picker so Set-location is one tap away.
//
// Gating (unchanged): parent still mounts us only when no pubkey AND
// no stored jurisdiction. Dismissal persists under the same localStorage
// key so returning users who closed the old welcome don't resee this.

const LS_KEY = 'verdant_welcome_dismissed'

const STEPS = [
  {
    key: 'intro',
    emoji: '🪶',
    title: 'Welcome to Verdant.',
    body: 'Vote Yes, No, or Unsure on bills, candidates, and ballot questions at every level of US government.',
  },
  {
    key: 'district',
    emoji: '📊',
    title: 'See how your district stacks up.',
    body: 'We separate resident votes from non-resident. You see how people who actually live in a jurisdiction are voting — right alongside the national view.',
  },
  {
    key: 'location',
    emoji: '📍',
    title: 'Make it personal.',
    body: 'Add your zip to unlock polls for your state and city, and to count your vote in your district tally.',
    cta: 'Set my location',
  },
]

export function WelcomeCard({ onSetLocation /* onSignIn kept in signature for compat */ }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1' }
    catch { return false }
  })
  const [step, setStep] = useState(0)
  const scrollRef = useRef(null)

  // Keep the dots indicator synced to actual scroll position so the
  // active dot moves when the user swipes with the finger (not just
  // our prev/next buttons).
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      const w = el.clientWidth || 1
      const idx = Math.round(el.scrollLeft / w)
      if (idx !== step) setStep(idx)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [step])

  if (dismissed) return null

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY, '1') } catch { /* noop */ }
    setDismissed(true)
  }

  const goTo = (idx) => {
    const el = scrollRef.current
    if (!el) return
    const clamped = Math.max(0, Math.min(STEPS.length - 1, idx))
    el.scrollTo({ left: el.clientWidth * clamped, behavior: 'smooth' })
    setStep(clamped)
  }

  const complete = () => {
    dismiss()
    onSetLocation?.()
  }

  return (
    <aside
      className="welcome-card welcome-intro"
      role="region"
      aria-labelledby="welcome-title"
    >
      <button
        type="button"
        className="welcome-close"
        aria-label="Dismiss"
        onClick={dismiss}
      >×</button>

      <div className="welcome-intro-scroll" ref={scrollRef}>
        {STEPS.map((s, i) => (
          <section
            key={s.key}
            className="welcome-intro-step"
            aria-current={step === i ? 'step' : undefined}
          >
            <div className="welcome-intro-emoji" aria-hidden="true">{s.emoji}</div>
            <h2
              id={i === 0 ? 'welcome-title' : undefined}
              className="welcome-intro-title"
            >{s.title}</h2>
            <p className="welcome-intro-body">{s.body}</p>
            {s.cta && (
              <button
                type="button"
                className="btn btn-primary welcome-intro-cta"
                onClick={complete}
              >
                {s.cta} →
              </button>
            )}
          </section>
        ))}
      </div>

      <div className="welcome-intro-footer">
        <button
          type="button"
          className="welcome-intro-nav"
          onClick={() => goTo(step - 1)}
          disabled={step === 0}
          aria-label="Previous step"
        >←</button>
        <div className="welcome-intro-dots" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`welcome-intro-dot${step === i ? ' active' : ''}`}
            />
          ))}
        </div>
        <button
          type="button"
          className="welcome-intro-nav"
          onClick={() => goTo(step + 1)}
          disabled={step === STEPS.length - 1}
          aria-label="Next step"
        >→</button>
      </div>
    </aside>
  )
}
